import { useTranslation } from "react-i18next";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import StationFeatureList from "../station-feature-list/station-feature-list";
import styles from "./station.module.less";
import { SecondaryButtonLink } from "../../../../../components/elements/button/button";
import StationContentSection from "../station-content-section/station-content-section";
import { TStation, TStationTrackingFunctions } from "../../../types/station-types";
import {
  doesStationHaveOpeningHours,
  getLabelForStationType,
  getLogoForStation,
  getOpeningHoursStringFromTimePeriod,
  getTodaysOpeningHoursForStationType,
  isStationManned,
  resolveElectricConnectorsForStation,
  resolveFuelsForStation,
  resolveServicesForStation,
} from "../../../helpers/station-data-resolvers";
import { isArrayWithItems } from "../../../../../helpers";
import { getMapLink } from "../../../helpers/map-helper";
import MapMarkerWrapper from "../station-markers/map-marker-wrapper/map-marker-wrapper";
import { getMarkerTypeForStation } from "../../../helpers/marker-helper";
import { TFunction } from "i18next";
import haversineDistance from "haversine-distance";
import { TPosition } from "../../../../../types/map-types";
import { Heading } from "../../../../../components/elements";
import { TTrailerRentalStationUrlResolveFunction } from "../../../find-station";

export type TStationProps = {
  station: TStation;
  geolocation?: TPosition;
  showStationMap?: boolean;
  mapApiKey?: string;
  trailerRentalPageUrl?: string;
  isStationPage?: boolean;
  trackingFunctions: TStationTrackingFunctions;
  trailerRentalStationUrlResolveFunction: TTrailerRentalStationUrlResolveFunction;
};

const MAX_ZOOM_LEVEL = 13;
const MIN_ZOOM_LEVEL = 5;

const DEFAULT_ZOOM = 10;

const getContentSectionText = (station: TStation, t: TFunction) => {
  const isManned = isStationManned(station);

  if (isManned) {
    return t("FIND_STATION_WELCOME_TO_REFUEL_24_7");
  }

  //if station isn't manned and has opening hours we don't show a text that you can refuel 24/7
  // - this is the case for såifa stations that are inside business complexes that close during weekends for example
  if (doesStationHaveOpeningHours(station)) {
    return "";
  }

  //if station is unmanned and doesn't have opening hours it is a 24/7 station
  return t("FIND_STATION_WELCOME_TO_REFUEL_24_7");
};

const getTrailerRentalPageUrlForStation = (
  station: TStation,
  trailerRentalStationUrlResolveFunction: TTrailerRentalStationUrlResolveFunction,
  trailerRentalPageUrl?: string,
) => {
  if (!trailerRentalPageUrl) {
    return undefined;
  }

  const stationRentalSlug = trailerRentalStationUrlResolveFunction(station.stationName);

  const hasTrailingSlash = trailerRentalPageUrl.endsWith("/");

  if (station.trailerRentalLocationId) {
    return `${trailerRentalPageUrl}${hasTrailingSlash ? "" : "/"}${stationRentalSlug}`;
  }
  return trailerRentalPageUrl;
};

export const Station = ({
  station,
  geolocation,
  showStationMap = false,
  mapApiKey,
  trailerRentalPageUrl,
  isStationPage,
  trackingFunctions,
  trailerRentalStationUrlResolveFunction,
}: TStationProps) => {
  const { t } = useTranslation();

  const stationServices = resolveServicesForStation(station, t);
  const fuels = resolveFuelsForStation(station, t);
  const electricConnectors = resolveElectricConnectorsForStation(station, t);
  const fuelsAndElectricConnectors = [...fuels, ...electricConnectors];

  const stationPosition = { lat: station.latitude, lng: station.longitude };

  const trailerRentalPageUrlForStation = getTrailerRentalPageUrlForStation(
    station,
    trailerRentalStationUrlResolveFunction,
    trailerRentalPageUrl,
  );

  const isManned = isStationManned(station);

  const distanceInMeters = geolocation
    ? haversineDistance(geolocation, { lat: station.latitude, lng: station.longitude })
    : null;

  const distanceInKm = distanceInMeters ? Math.round(distanceInMeters / 1000) : null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.sectionWrapper}>
        <div className={styles.topSection}>
          <span className={"eyebrow"}>
            {getLabelForStationType(station.stationType, t)} - {getTodaysOpeningHoursForStationType(station, t)}
          </span>
          <div className={styles.stationNameWrapper}>
            <Heading
              visualType={"h3"}
              semanticType={isStationPage ? "h1" : "h3"}
              className={["my-0", styles.stationName].join(" ")}
            >
              {station.stationName}
            </Heading>
            <div className={styles.stationIcons}>{getLogoForStation(station.stationType, station.stationConcept)}</div>
          </div>
          <div className={styles.adressAndDistance}>
            <div className={["mb-0"].join("")}>
              {station.streetAddress}, {station.city}
            </div>
            {distanceInMeters && (
              <>
                <div>•</div>
                <div className={"mb-0"}>
                  {distanceInKm} {t("FIND_STATION_KM_ABBREVIATED")}
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <SecondaryButtonLink
            label={t("FIND_STATION_DIRECTIONS_CTA")}
            openInNewTab={true}
            className={styles.button}
            url={getMapLink(station.streetAddress + ", " + station.postalCode + ", " + station.city)}
            onPreNavigate={trackingFunctions.onPreNavigateStationDirections}
          />
          {station.phoneNumber && (
            <SecondaryButtonLink
              className={styles.button}
              label={station.phoneNumber}
              url={`tel:${station.phoneNumber}`}
              onPreNavigate={trackingFunctions.onPreNavigatePhoneNumber}
            />
          )}
          {station.trailerRental && station.trailerRentalLocationId && trailerRentalPageUrlForStation && (
            <SecondaryButtonLink
              url={trailerRentalPageUrlForStation}
              className={[styles.trailerRentalButton, styles.button].join(" ")}
              isFullWidth={true}
              label={t("FIND_STATION_TRAILER_RENTAL_CTA")}
              onPreNavigate={trackingFunctions.onPreNavigateTrailerRental}
            />
          )}
        </div>
        <StationContentSection
          title={isManned ? t("FIND_STATION_STORE_OPENING_HOURS") : t("OPENING_HOURS")}
          text={getContentSectionText(station, t)}
          isStationPage={isStationPage}
        >
          {doesStationHaveOpeningHours(station) && (
            <dl className={styles.openingHours}>
              <dt>
                {t("MONDAY").toLowerCase()}–{t("FRIDAY").toLowerCase()}
              </dt>
              <dd>
                {getOpeningHoursStringFromTimePeriod(
                  station.openingHours.openingHourWeekday,
                  station.openingHours.closeHourWeekday,
                  t,
                )}
              </dd>
              <dt>{t("SATURDAY").toLowerCase()}</dt>
              <dd>
                {getOpeningHoursStringFromTimePeriod(
                  station.openingHours.openingHourSaturday,
                  station.openingHours.closeHourSaturday,
                  t,
                )}
              </dd>
              <dt>{t("SUNDAY").toLowerCase()}</dt>
              <dd>
                {getOpeningHoursStringFromTimePeriod(
                  station.openingHours.openingHourSunday,
                  station.openingHours.closeHourSunday,
                  t,
                )}
              </dd>
            </dl>
          )}
        </StationContentSection>
        {isArrayWithItems(stationServices) && (
          <StationContentSection
            title={t("FIND_STATION_SERVICES_TITLE")}
            text={t("FIND_STATION_SERVICES_TEXT")}
            isStationPage={isStationPage}
          >
            <StationFeatureList items={stationServices} />
          </StationContentSection>
        )}
        {isArrayWithItems(fuelsAndElectricConnectors) && (
          <StationContentSection
            title={t("FIND_STATION_PROPELLANTS_TITLE")}
            text={t("FIND_STATION_PROPELLANTS_TEXT")}
            isStationPage={isStationPage}
          >
            <StationFeatureList items={fuelsAndElectricConnectors} />
          </StationContentSection>
        )}
      </div>
      {showStationMap && mapApiKey && (
        <div className={styles.mapWrapper}>
          <APIProvider apiKey={mapApiKey} libraries={["marker"]}>
            <Map
              id={"station-map-page"}
              defaultZoom={DEFAULT_ZOOM}
              defaultCenter={stationPosition}
              maxZoom={MAX_ZOOM_LEVEL}
              minZoom={MIN_ZOOM_LEVEL}
              gestureHandling={"cooperative"}
              disableDefaultUI={true}
              mapTypeId={"roadmap"}
              mapId={"3ab6c52371682fb5"}
              clickableIcons={false}
            >
              <MapMarkerWrapper
                stationCode={station.stationCode}
                position={stationPosition}
                markerType={getMarkerTypeForStation(station)}
              />
            </Map>
          </APIProvider>
        </div>
      )}
    </div>
  );
};

export default Station;
